import { forwardRef, useMemo, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { BrushStroke, SprayFlow, prepareSpray, DEFAULT_BRUSH, DrawingHistory, hasPaint, sameDrawingPixels, paintDabs, rotateDrawingPixels, type BrushPoint, type BrushSettings, type RotationDirection } from './brush';
import {completedDrawingTool,hasAuthoredWallChange} from './achievementPixels';
import type {DrawingAchievementTool} from '../shared/achievements';
import ColorPicker from './ColorPicker';
import { adjustFillSession, FillEdit, type FillAdjustmentSession } from './fillEdit';
import { snapshotWallFill, type WallArtworkBackdrop } from './wallArtwork';
import { shapeDabs, type DrawingShape } from './drawingShapes';
import './painting-dock.css';
import { DEFAULT_DRAWING_SHADOW, drawingCompositeBlob, renderDrawingShadow, type DrawingShadow } from './drawingShadow';
import type { Asset, Draft, PaintOrder, Surface } from '../shared/types';
import {surfaceRasterMask} from '../shared/surfaceRegions';
import {clipSurfaceCanvas} from './surfaceCanvasClip';
import { loadPreparedSourceImage,renderPreparedSourceLayer,sourceLayerKey,type PreparedSourceLayer,type PreparedSourceEffects,type PreparedSourceComposite } from './sourceLayer';

export interface DrawingState { canvas: HTMLCanvasElement; shadow?: HTMLCanvasElement; pixelRevision: number; drawn: boolean; canUndo: boolean; canRedo: boolean; drawing: boolean }
export interface DrawingViewGesture { phase: 'start' | 'move' | 'end'; origin: { x: number; y: number }; point: { x: number; y: number }; scale: number }
export interface DrawingCanvasHandle {
  getCanvas(): HTMLCanvasElement | null;
  getSourceBaseSnapshot(): Pick<PreparedSourceComposite,'base'|'revision'|'pixels'> | null;
  getBlob(): Promise<Blob | null>;
  undo(): void; redo(): void; clear(): void; reset(): void; rotate(direction: RotationDirection): void;
  beginViewNavigation(): void; endViewNavigation(): void;
  interruptSpray(): void;
  placeAsset(asset: Asset, draft: Draft, effects?: PreparedSourceEffects, prepared?:PreparedSourceLayer|Promise<PreparedSourceLayer>): Promise<number|undefined>; endFill(): void;
}
interface DrawingCanvasProps {
  surface?: Surface; settings: BrushSettings; busy?: boolean; inputDisabled?: boolean; width?: number; height?: number;
  sourceContext?: string;
  initialImage?: string | null; onChange?: (state: DrawingState) => void;
  onAuthored?: (family: 'draw' | 'stamp' | 'image', asset?: Asset) => void;
  onToolUsed?: (tool:DrawingAchievementTool) => void;
  onRemember?: (image: string, dimensions: { width: number; height: number }) => void; className?: string;
  panning?: boolean; onViewGesture?: (gesture: DrawingViewGesture) => void;
  wallAwareFill?: boolean; fillBackdrop?: HTMLCanvasElement | WallArtworkBackdrop | null; onFillError?: (message: string) => void;
  paintOrder?: PaintOrder;
  belowPlacementId?: string;
  shadow?: DrawingShadow;
  shape?: DrawingShape | null;
  authoringFamily?: 'draw' | 'stamp' | 'image'; onPlace?: (point: BrushPoint) => void;

}

export const DrawingCanvas = forwardRef<DrawingCanvasHandle, DrawingCanvasProps>(function DrawingCanvas(
  { surface: wallSurface, settings, busy = false, inputDisabled = false, width = 768, height = 384, sourceContext = '', initialImage, onChange, onAuthored, onToolUsed, onRemember, className = '', panning = false, onViewGesture, wallAwareFill = false, fillBackdrop, onFillError, paintOrder = 'over', belowPlacementId, shadow = DEFAULT_DRAWING_SHADOW, shape = null, authoringFamily = 'draw', onPlace }, ref) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const wallInput = useRef<HTMLDivElement>(null);
  // Safari can hit the HTML wrapper instead of its canvas inside foreignObject.
  // Input/capture belong to one owner; pixels and coordinates always use canvas.
  const inputOwner = () => wallSurface ? wallInput.current : canvas.current;
  const releasePointer = (id: number) => {
    const owner = inputOwner();
    if (owner?.hasPointerCapture(id)) owner.releasePointerCapture(id);
  };
  const capturePointer = (id: number) => {
    const owner = inputOwner();
    if (!owner?.isConnected) return false;
    try { owner.setPointerCapture(id); return owner.hasPointerCapture(id); }
    catch { return false; }
  };
  const [dimensions, setDimensions] = useState({ width, height });
  // Snapshots replace surface objects even when their physical target is the
  // same. Compare its paint geometry, without serializing it on every frame.
  const spraySurface = useMemo(() => wallSurface ? JSON.stringify([wallSurface.id, wallSurface.blockId, wallSurface.x, wallSurface.y, wallSurface.width, wallSurface.height, wallSurface.role, wallSurface.regions, wallSurface.contours, wallSurface.reservePanels, wallSurface.panelReservations, wallSurface.exteriorOcclusions, wallSurface.exteriorClip]) : '', [wallSurface]);
  // A live Spray gesture belongs to this setup; changes cancel it before
  // another timed deposit can use a new tool, target or effect.
  const spraySetup = useMemo(() => ({}), [settings.kind, settings.color, settings.width, settings.opacity, spraySurface, dimensions.width, dimensions.height, shape, authoringFamily, shadow.style, shadow.distance, paintOrder, belowPlacementId]);
  const wallMask = useMemo(() => wallSurface&&(wallSurface.regions||wallSurface.contours||wallSurface.reservePanels||wallSurface.role==='exclusion') ? surfaceRasterMask(wallSurface, dimensions.width, dimensions.height) : undefined, [wallSurface,dimensions.width,dimensions.height]);
  const paint = (context: CanvasRenderingContext2D, dabs: Parameters<typeof paintDabs>[1], brush: BrushSettings) => {
    const layerContext = active.current?.layer?.getContext('2d');
    for (const target of layerContext && layerContext !== context ? [layerContext, context] : [context]) {
      if (!wallSurface||(wallSurface.regions===undefined&&wallSurface.contours===undefined&&!wallSurface.reservePanels&&wallSurface.role!=='exclusion')) { paintDabs(target, dabs, brush); continue; }
      target.save(); clipSurfaceCanvas(target,wallSurface,dimensions.width,dimensions.height); paintDabs(target, dabs, brush); target.restore();
    }
  };
  const pendingImage = useRef<ImageData | null>(null);
  const pendingMutation = useRef(true);
  const pixelRevision = useRef(0);
  const sourceGeneration = useRef(0);
  const drawn = useRef(false);
  const loadingImage = useRef(false);
  const history = useRef<DrawingHistory<ImageData> | null>(null);
  const nativeSourceSnapshot = useRef<Pick<PreparedSourceComposite,'base'|'revision'|'pixels'> | null>(null);
  const activeFill = useRef<FillAdjustmentSession | null>(null);
  const active = useRef<({ id: number; engine: BrushStroke; sprayFlow: SprayFlow | null; spraySetup: object; start: BrushPoint; clientStart: BrushPoint; moved: boolean; scanned: boolean; fillReference: ImageData | null; shape: DrawingShape | null; seed: number; layer: HTMLCanvasElement | null }
    & ({placing:true;before:null}|{placing:false;before:ImageData})) | null>(null);
  const sprayFrame = useRef<number | null>(null);
  const sprayTick = useRef<() => void>(() => {});
  const stopSprayFrame = () => {
    if (sprayFrame.current !== null) cancelAnimationFrame(sprayFrame.current);
    sprayFrame.current = null;
  };
  const scheduleSprayFrame = () => {
    if (sprayFrame.current === null) sprayFrame.current = requestAnimationFrame(() => sprayTick.current());
  };
  const blocked = useRef(false);
  const externalNavigation = useRef(false);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const navigation = useRef<{ origin: { x: number; y: number }; distance: number } | null>(null);
  const live = useRef({ onChange, onAuthored, onToolUsed, onRemember, busy, inputDisabled, panning, settings, shape, authoringFamily, sourceContext, wallSurface });
  live.current = { onChange, onAuthored, onToolUsed, onRemember, busy, inputDisabled, panning, settings, shape, authoringFamily, sourceContext, wallSurface };
  const notify = (pixelsChanged = false, exact = false, snapshot?: ImageData) => {
    const surface = canvas.current, context = surface?.getContext('2d');
    if (!surface || !context || !history.current) return;
    if (pixelsChanged) { nativeSourceSnapshot.current=null;pixelRevision.current++; }
    // Actions are locked during a stroke. Read its initial visibility once, then
    // inspect the exact finished/restored pixels instead of copying megabytes
    // on every pointer move (including eraser strokes).
    const stroke = active.current;
    if ((pixelsChanged || exact) && (exact || !stroke?.scanned)) {
      drawn.current = hasPaint((snapshot ?? context.getImageData(0, 0, surface.width, surface.height)).data);
      if (stroke) stroke.scanned = true;
    }
    live.current.onChange?.({ canvas: surface, pixelRevision: pixelRevision.current, drawn: drawn.current, canUndo: history.current.canUndo, canRedo: history.current.canRedo, drawing: !!stroke&&!stroke.placing || externalNavigation.current || !!navigation.current });
  };
  const applyImage = (image: ImageData, pixelsChanged = true) => {
    const surface = canvas.current;
    if (!surface) return;
    if (surface.width !== image.width || surface.height !== image.height) {
      pendingImage.current = image; pendingMutation.current = pixelsChanged; blocked.current = true;
      setDimensions({ width: image.width, height: image.height });
    } else {
      // The only non-mutating restore initializes a known blank raster. Its
      // visibility was reset already; avoid reading and scanning it back.
      const context = surface.getContext('2d');
      if (pixelsChanged) context?.putImageData(image, 0, 0);
      else context?.clearRect(0, 0, surface.width, surface.height);
      notify(pixelsChanged, pixelsChanged, image);
    }
  };
  // React owns backing dimensions; restore pixels after its resize clears the canvas.
  useLayoutEffect(() => {
    const image = pendingImage.current;
    if (!image) return;
    const surface = canvas.current, context = surface?.getContext('2d');
    if (pendingMutation.current) context?.putImageData(image, 0, 0);
    else if (surface) context?.clearRect(0, 0, surface.width, surface.height);
    pendingImage.current = null; blocked.current = loadingImage.current; notify(pendingMutation.current, pendingMutation.current, image);
  }, [dimensions.width, dimensions.height]);
  const cancel = (preservePointers = false) => {
    stopSprayFrame();
    const stroke = active.current;
    active.current = null;
    if (stroke) {
      stroke.sprayFlow?.cancel();
      if(!stroke.placing)canvas.current?.getContext('2d')?.putImageData(stroke.before, 0, 0);
      releasePointer(stroke.id);
    }
    const gesture = navigation.current;
    if (!preservePointers) {
      const ids = [...pointers.current.keys()];
      navigation.current = null;
      pointers.current.clear();
      for (const id of ids) releasePointer(id);
      if (gesture) onViewGesture?.({ phase: 'end', origin: gesture.origin, point: gesture.origin, scale: 1 });
    }
    if (stroke&&!stroke.placing || gesture) notify(!!stroke&&!stroke.placing, !!stroke&&!stroke.placing);
  };
  // The scheduled callback reads this render's guards/geometry, not the props
  // captured when the pointer went down. Flow alone controls the emission rate.
  sprayTick.current = () => {
    sprayFrame.current = null;
    const stroke = active.current, surface = canvas.current;
    if (!stroke?.sprayFlow) return;
    // Capture can be released before the browser delivers lostpointercapture,
    // especially during a stationary hold with no new pointer events.
    if (!surface || !inputOwner()?.hasPointerCapture(stroke.id) || surface.ownerDocument.hidden || busy || inputDisabled || panning || blocked.current || externalNavigation.current || navigation.current || stroke.spraySetup !== spraySetup) { cancel(); return; }
    const context = surface.getContext('2d');
    if (!context) { cancel(); return; }
    const dabs = stroke.sprayFlow.advance(performance.now());
    if (dabs.length) { paint(context, dabs, stroke.engine.settings); notify(true); }
    if (active.current === stroke) scheduleSprayFrame();
  };
  const restore = (direction: 'undo' | 'redo') => {
    if (live.current.busy || blocked.current || active.current) return;
    activeFill.current = null;
    const image = history.current?.[direction]();
    if (image) applyImage(image);
  };
  const clear = () => {
    if (live.current.busy || blocked.current || active.current) return;
    activeFill.current = null;
    const surface = canvas.current, context = surface?.getContext('2d');
    if (!surface || !context) return;
    context.clearRect(0, 0, surface.width, surface.height);
    history.current?.commit(context.getImageData(0, 0, surface.width, surface.height)); notify(true, true);
  };
  // Lifecycle completion is not an undoable drawing edit. It also runs while
  // Paint is acknowledging a locked snapshot; ordinary Clear stays reversible.
  const reset = () => {
    sourceGeneration.current++;
    cancel();activeFill.current=null;externalNavigation.current=false;navigation.current=null;pointers.current.clear();
    const surface=canvas.current,context=surface?.getContext('2d');
    if(!surface||!context)return;
    context.clearRect(0,0,surface.width,surface.height);
    const blank=context.createImageData(surface.width,surface.height);
    history.current=new DrawingHistory(blank);notify(true,true,blank);
  };
  const rotate = (direction: RotationDirection) => {
    if (live.current.busy || blocked.current || active.current) return;
    activeFill.current = null;
    const surface = canvas.current, context = surface?.getContext('2d');
    if (!surface || !context) return;
    const rotated = rotateDrawingPixels(context.getImageData(0, 0, surface.width, surface.height), direction);
    const image = new ImageData(rotated.data, rotated.width, rotated.height);
    history.current?.commit(image); applyImage(image);
  };
  const placeAsset = async (asset: Asset, draft: Draft, effects: PreparedSourceEffects = { shadow, frame: 'none' }, prepared?:PreparedSourceLayer|Promise<PreparedSourceLayer>) => {
    if (live.current.busy || blocked.current || active.current || !wallSurface) throw new Error('Finish the current action before placing artwork.');
    if (draft.surfaceId !== wallSurface.id || draft.assetId !== asset.id) throw new Error('This artwork belongs to another target. Your drawing is safe.');
    const target = canvas.current, context = target?.getContext('2d');
    if (!target || !context) throw new Error('The drawing is unavailable. Your source is still ready.');
    const sourceFamily = live.current.authoringFamily;
    const generation=sourceGeneration.current;
    const key=sourceLayerKey({context:sourceContext,asset,surface:wallSurface,draft,width:target.width,height:target.height,effects});
    activeFill.current = null;
    blocked.current = true;
    try {
      // Claim the editor before waiting: App's own busy render must not reject
      // the source finalization that acquired this lock.
      const supplied=prepared instanceof Promise?await prepared:prepared;
      const retained=supplied?.key===key&&supplied.layer.width===target.width&&supplied.layer.height===target.height?supplied:undefined;
      const layer=retained?.layer??renderPreparedSourceLayer(await loadPreparedSourceImage(asset.url),wallSurface,draft,target.width,target.height,effects);
      const current=live.current;
      if(canvas.current!==target||generation!==sourceGeneration.current||!current.wallSurface||sourceLayerKey({context:current.sourceContext,asset,surface:current.wallSurface,draft,width:target.width,height:target.height,effects})!==key)throw new Error('The painting target changed. Your source is still ready.');
      const composite=retained?.composite;
      const matching=composite?.base===target&&composite.revision===pixelRevision.current&&composite.pixels.width===target.width&&composite.pixels.height===target.height?composite:undefined;
      let finished:ImageData,changed:boolean,authored:boolean;
      if(matching){
        finished=matching.pixels;changed=matching.changed;authored=matching.authored;
        // Keep native source-over pixels. Putting unpremultiplied ImageData back
        // can round translucent RGB a second time, unlike the original bake.
        context.drawImage(layer,0,0);
      }else{
        const before=context.getImageData(0,0,target.width,target.height);
        context.drawImage(layer,0,0);finished=context.getImageData(0,0,target.width,target.height);
        changed=!sameDrawingPixels(before.data,finished.data);authored=hasAuthoredWallChange(before.data,finished.data);
      }
      if (changed) history.current?.commit(finished);
      notify(changed, true, finished);
      // Only source bakes supply a verified native snapshot. Algorithm-produced
      // fill/restore pixels must pass through the ordinary canvas readback.
      if(changed&&history.current?.current===finished)nativeSourceSnapshot.current={base:target,revision:pixelRevision.current,pixels:finished};
      if(authored)live.current.onAuthored?.(sourceFamily,asset);
      return matching?.estimatedInk;
    } finally { blocked.current = false; }
  };
  useImperativeHandle(ref, () => ({ getCanvas: () => canvas.current,
    getSourceBaseSnapshot: () => {
      const snapshot=nativeSourceSnapshot.current,target=canvas.current;
      if(!snapshot||!target||active.current||blocked.current||loadingImage.current||pendingImage.current||externalNavigation.current||navigation.current||live.current.busy
        ||snapshot.base!==target||snapshot.revision!==pixelRevision.current||snapshot.pixels!==history.current?.current||snapshot.pixels.width!==target.width||snapshot.pixels.height!==target.height)return null;
      return snapshot;
    },
    getBlob: () => {
      if (!canvas.current || active.current || blocked.current || externalNavigation.current || navigation.current) return Promise.resolve(null);
      return drawingCompositeBlob(canvas.current);
    },
    undo: () => restore('undo'), redo: () => restore('redo'), clear, reset, rotate, placeAsset,
    interruptSpray: () => interruptSpray.current(),
    endFill: () => { activeFill.current = null; },
    beginViewNavigation: () => { cancel(); externalNavigation.current = true; pointers.current.clear(); notify(); },
    endViewNavigation: () => { externalNavigation.current = false; pointers.current.clear(); notify(); } }));
  // A lazily mounted wall editor must be initialized before its first pointer
  // can arrive; passive initialization could clear that first stroke afterward.
  useLayoutEffect(() => {
    sourceGeneration.current++;
    nativeSourceSnapshot.current=null;
    const surface = canvas.current;
    // Select this on the first context creation: Safari's accelerated canvas
    // can lose SVG foreignObject transforms/clipping when displaying live paint.
    // Wall editing also reads pixels for masks/history; Studio keeps its default.
    const context = surface?.getContext('2d', wallSurface ? { willReadFrequently: true } : undefined);
    if (!surface || !context) return;
    let alive = true;
    loadingImage.current = !!initialImage;
    activeFill.current = null;
    pixelRevision.current = 0; drawn.current = false;
    const blank = context.createImageData(width, height);
    history.current = new DrawingHistory(blank);
    const finish = (snapshot: ImageData) => { if (!alive) return; loadingImage.current = false; blocked.current = false; history.current = new DrawingHistory(snapshot); applyImage(snapshot, !!initialImage); };
    if (initialImage) {
      applyImage(blank, false);
      blocked.current = true;
      const image = new Image();
      image.onload = () => { if (!alive) return; const buffer = document.createElement('canvas'); buffer.width = width; buffer.height = height; const loaded = buffer.getContext('2d'); if (!loaded) { finish(blank); return; } loaded.drawImage(image, 0, 0, width, height); finish(loaded.getImageData(0, 0, width, height)); };
      image.onerror = () => finish(blank);
      image.src = initialImage;
    } else finish(blank);
    return () => {
      sourceGeneration.current++;
      nativeSourceSnapshot.current=null;
      alive = false;
      stopSprayFrame();
      active.current?.sprayFlow?.cancel();
      if (active.current&&!active.current.placing) context.putImageData(active.current.before, 0, 0);
      active.current = null;
      const ids = [...pointers.current.keys()];
      pointers.current.clear(); navigation.current = null;
      for (const id of ids) releasePointer(id);
      live.current.onRemember?.(surface.toDataURL('image/png'), { width: surface.width, height: surface.height });
    };
    // Initial content belongs to this canvas instance; settings and callbacks remain live.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height]);
  useEffect(() => { if (busy) cancel(); }, [busy]);
  useEffect(() => { if (inputDisabled) cancel(); }, [inputDisabled]);
  useLayoutEffect(() => {
    const stroke = active.current;
    if (stroke?.sprayFlow && (busy || inputDisabled || panning || stroke.spraySetup !== spraySetup)) cancel();
  }, [spraySetup, busy, inputDisabled, panning]);
  const warmingBlocked = !!active.current || blocked.current || externalNavigation.current || !!navigation.current;
  useEffect(() => {
    const surface = canvas.current;
    if (!surface || settings.kind !== 'spray' || shape || authoringFamily !== 'draw' || busy || inputDisabled || panning || warmingBlocked || surface.ownerDocument.hidden) return;
    const color = settings.color;
    return prepareSpray(surface.ownerDocument, color, () => {
      const current = live.current;
      return canvas.current === surface && !surface.ownerDocument.hidden && !active.current && !blocked.current && !externalNavigation.current && !navigation.current && !current.busy && !current.inputDisabled && !current.panning && current.settings.kind === 'spray' && current.settings.color === color && !current.shape && current.authoringFamily === 'draw';
    });
  }, [settings.kind, settings.color, shape, authoringFamily, busy, inputDisabled, panning, warmingBlocked]);
  const interruptSpray = useRef<() => void>(() => {});
  interruptSpray.current = () => {
    if (!active.current?.sprayFlow) return;
    cancel(); pointers.current.clear();
  };
  useEffect(() => {
    const owner = canvas.current?.ownerDocument;
    if (!owner) return;
    const hidden = () => { if (owner.hidden) interruptSpray.current(); };
    const blur = () => interruptSpray.current();
    owner.addEventListener('visibilitychange', hidden);
    owner.defaultView?.addEventListener('blur', blur);
    return () => { owner.removeEventListener('visibilitychange', hidden); owner.defaultView?.removeEventListener('blur', blur); };
  }, []);
  useLayoutEffect(() => {
    const fill = activeFill.current;
    if (!fill || busy || blocked.current || active.current) return;
    const adjustment=adjustFillSession(fill,{kind:settings.kind,shape:!!shape,color:settings.color,wallAware:wallAwareFill,paintOrder,belowPlacementId,opacity:settings.opacity});
    activeFill.current=adjustment.session;
    if(!adjustment.pixels)return;
    const pixels=adjustment.pixels;
    const image = new ImageData(pixels.data, pixels.width, pixels.height);
    history.current?.replaceCurrent(image);
    applyImage(image);
  }, [settings.kind, settings.color, settings.opacity, shape, wallAwareFill, paintOrder, belowPlacementId, busy]);
  const pointerCentre = () => {
    const points = [...pointers.current.values()];
    return points.length > 1 ? { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 } : points[0];
  };
  const pointerDistance = () => {
    const points = [...pointers.current.values()];
    return points.length > 1 ? Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y) : 0;
  };
  const startNavigation = () => {
    cancel(true);
    const origin = pointerCentre();
    if (!origin) return;
    if (![...pointers.current.keys()].every(capturePointer)) { cancel(); return; }
    navigation.current = { origin, distance: pointerDistance() };
    onViewGesture?.({ phase: 'start', origin, point: origin, scale: 1 });
    notify();
  };
  const endNavigationPointer = (id: number) => {
    pointers.current.delete(id);
    if (!navigation.current) return false;
    if (pointers.current.size) startNavigation();
    else {
      const { origin } = navigation.current;
      navigation.current = null;
      onViewGesture?.({ phase: 'end', origin, point: origin, scale: 1 });
      notify();
    }
    return true;
  };
  const position = (event: { clientX: number; clientY: number }): BrushPoint | null => {
    const target = canvas.current;
    if (!target) return null;
    if (wallSurface) {
      // The canvas fills this SVG rectangle. Map in SVG space so embedded HTML
      // bounds cannot introduce a browser-dependent offset or scale.
      const frame = target.closest('foreignObject') as SVGForeignObjectElement | null;
      const matrix = frame?.getScreenCTM();
      if (!frame || !matrix || ![matrix.a, matrix.b, matrix.c, matrix.d, matrix.e, matrix.f].every(Number.isFinite) || !Number.isFinite(matrix.a * matrix.d - matrix.b * matrix.c) || matrix.a * matrix.d - matrix.b * matrix.c === 0) return null;
      const { x, y, width, height } = frame;
      if (![x.baseVal.value, y.baseVal.value, width.baseVal.value, height.baseVal.value].every(Number.isFinite) || width.baseVal.value <= 0 || height.baseVal.value <= 0) return null;
      const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix.inverse());
      const mapped = { x: (point.x - x.baseVal.value) * target.width / width.baseVal.value, y: (point.y - y.baseVal.value) * target.height / height.baseVal.value };
      return Number.isFinite(mapped.x) && Number.isFinite(mapped.y) ? mapped : null;
    }
    const bounds = target.getBoundingClientRect();
    return { x: (event.clientX - bounds.left) * target.width / bounds.width, y: (event.clientY - bounds.top) * target.height / bounds.height };
  };
  const pointerHandlers: React.HTMLAttributes<HTMLElement> = {
    onPointerDown: event => {
      if (busy || inputDisabled || blocked.current || externalNavigation.current || (event.pointerType === 'mouse' && event.button !== 0)) return;
      pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      // Navigation cancels the in-progress stroke and consumes remaining fingers
      // until all have lifted; changing zoom never resizes the backing canvas.
      if (panning || pointers.current.size > 1 || navigation.current) { startNavigation(); return; }
      if (!event.isPrimary || active.current) { cancel(); return; }
      const surface = canvas.current, context = surface?.getContext('2d');
      if (!surface || !context) { pointers.current.delete(event.pointerId); return; }
      const point=position(event);
      if (!point) { pointers.current.delete(event.pointerId); return; }
      if (wallSurface && (point.x < 0 || point.y < 0 || point.x >= surface.width || point.y >= surface.height)) { pointers.current.delete(event.pointerId); return; }
      if(wallMask && !wallMask[Math.floor(point.y)*dimensions.width+Math.floor(point.x)]){pointers.current.delete(event.pointerId);return;}
      const placing = authoringFamily !== 'draw';
      const engine = new BrushStroke(shape ? { ...settings, kind: shape.style } : settings);
      const sprayFlow = !placing && !shape && engine.settings.kind === 'spray' ? new SprayFlow(engine.settings) : null;
      if (settings.kind !== 'fill' || shape || placing) activeFill.current = null;
      let fillReference: ImageData | null = null;
      const repeatedFill = settings.kind === 'fill' && activeFill.current?.edit.contains(point);
      if (!placing && !shape && settings.kind === 'fill' && wallAwareFill && !repeatedFill) {
        if (!fillBackdrop) { pointers.current.delete(event.pointerId); onFillError?.('Wait for saved wall artwork to load before filling. Your drawing is safe.'); return; }
        try { fillReference = snapshotWallFill(fillBackdrop, surface, paintOrder); }
        catch (error) { pointers.current.delete(event.pointerId); onFillError?.(error instanceof Error ? error.message : 'Wall artwork is unavailable. Retry the fill.'); return; }
      }
      let layer: HTMLCanvasElement | null = null;
      if (!placing && shadow.style !== 'off' && !['fill','eraser'].includes(engine.settings.kind)) { layer = document.createElement('canvas'); layer.width = surface.width; layer.height = surface.height; }
      // Choosing a source position does not mutate the private drawing. Keep
      // pointer ownership, without a stroke snapshot or completion notification.
      const snapshot=placing?{placing:true as const,before:null}:{placing:false as const,before:context.getImageData(0,0,surface.width,surface.height)};
      if (!capturePointer(event.pointerId)) { pointers.current.delete(event.pointerId); return; }
      active.current = { id: event.pointerId, engine, sprayFlow, spraySetup, ...snapshot, start: point, clientStart: { x: event.clientX, y: event.clientY }, moved: false, scanned: false, fillReference, shape, seed: Date.now(), layer };
      const dabs = sprayFlow ? sprayFlow.start(point, performance.now()) : placing || shape || engine.settings.kind === 'fill' ? [] : engine.start(point);
      if (dabs.length) paint(context, dabs, engine.settings);
      if(!placing)notify(dabs.length > 0);
      if (sprayFlow && active.current?.sprayFlow === sprayFlow) scheduleSprayFrame();
    },
    onPointerMove: event => {
      if (externalNavigation.current) return;
      if (pointers.current.has(event.pointerId)) pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (navigation.current) {
        if (!busy) onViewGesture?.({ phase: 'move', origin: navigation.current.origin, point: pointerCentre(), scale: navigation.current.distance > 0 ? pointerDistance() / navigation.current.distance : 1 });
        return;
      }
      const stroke = active.current;
      if (busy || !stroke || stroke.id !== event.pointerId) return;
      if (stroke.placing) { if (Math.hypot(event.clientX - stroke.clientStart.x, event.clientY - stroke.clientStart.y) > 8) stroke.moved = true; return; }
      const point = position(event);
      if (!point) { pointers.current.delete(event.pointerId); cancel(); return; }
      if (stroke.sprayFlow) { stroke.sprayFlow.move(point); return; }
      if (stroke.shape) {
        const context = canvas.current?.getContext('2d'); if (!context) { cancel(); return; }
        context.putImageData(stroke.before, 0, 0);
        stroke.layer?.getContext('2d')?.clearRect(0, 0, dimensions.width, dimensions.height);
        const result = shapeDabs(stroke.shape, stroke.start, point, stroke.engine.settings, stroke.seed);
        paint(context, result.dabs, result.settings); notify(true); return;
      }
      if (stroke.engine.settings.kind === 'fill') { if (Math.hypot(event.clientX - stroke.clientStart.x, event.clientY - stroke.clientStart.y) > 8) stroke.moved = true; return; }
      const context = canvas.current?.getContext('2d');
      if (!context) { cancel(); return; }
      const dabs = stroke.engine.move(point);
      if (dabs.length) paint(context, dabs, stroke.engine.settings);
      notify(dabs.length > 0);
    },
    onPointerUp: event => {
      if (externalNavigation.current) return;
      if (endNavigationPointer(event.pointerId)) {
        releasePointer(event.pointerId);
        return;
      }
      const stroke = active.current;
      if (!stroke || stroke.id !== event.pointerId) return;
      stopSprayFrame();
      const surface = canvas.current, context = surface?.getContext('2d');
      if (!surface || !context) { cancel(); return; }
      if (stroke.placing) {
        active.current = null;
        releasePointer(event.pointerId);
        if (!busy && !stroke.moved && Math.hypot(event.clientX - stroke.clientStart.x, event.clientY - stroke.clientStart.y) <= 8 && wallSurface) onPlace?.({ x: stroke.start.x * wallSurface.width / surface.width, y: stroke.start.y * wallSurface.height / surface.height });
        return;
      }
      const point = position(event);
      if (!point) { cancel(); return; }
      let changed = true;
      if (stroke.shape) {
        context.putImageData(stroke.before, 0, 0);
        stroke.layer?.getContext('2d')?.clearRect(0, 0, dimensions.width, dimensions.height);
        const result = shapeDabs(stroke.shape, stroke.start, point, stroke.engine.settings, stroke.seed);
        paint(context, result.dabs, result.settings);
      } else if (stroke.engine.settings.kind === 'fill') {
        changed = false;
        if (!busy && !stroke.moved && Math.hypot(event.clientX - stroke.clientStart.x, event.clientY - stroke.clientStart.y) <= 8 && !activeFill.current?.edit.contains(stroke.start)) {
          const edit = FillEdit.create(stroke.before, stroke.start, stroke.engine.settings.color, stroke.fillReference?.data, wallMask);
          if (edit) {
            const pixels = edit.render(stroke.engine.settings.opacity);
            const image = new ImageData(pixels.data, pixels.width, pixels.height);
            changed = !sameDrawingPixels(image.data, stroke.before.data);
            if (changed) {
              context.putImageData(image, 0, 0);
              activeFill.current = { edit, color: stroke.engine.settings.color, wallAware: wallAwareFill, paintOrder, belowPlacementId, opacity: stroke.engine.settings.opacity };
            }
          }
        }
      } else {
        const dabs = stroke.sprayFlow ? stroke.sprayFlow.finish(point, performance.now()) : stroke.engine.finish(point);
        if (dabs.length) paint(context, dabs, stroke.engine.settings);
        changed = dabs.length > 0;
      }
      if (stroke.layer) {
        const effect = document.createElement('canvas'); renderDrawingShadow(stroke.layer, effect, shadow, wallSurface);
        context.putImageData(stroke.before, 0, 0);
        if (effect.width) context.drawImage(effect, 0, 0, dimensions.width, dimensions.height);
        context.drawImage(stroke.layer, 0, 0);
        stroke.layer.width = 0; stroke.layer.height = 0;
        changed = true;
      }
      active.current = null;
      const finished=context.getImageData(0,0,surface.width,surface.height);
      changed=!sameDrawingPixels(finished.data,stroke.before.data);
      if(changed)history.current?.commit(finished);
      releasePointer(event.pointerId);
      notify(changed, true, finished);
      if (wallSurface && hasAuthoredWallChange(stroke.before.data, finished.data, stroke.engine.settings.kind === 'eraser')) live.current.onAuthored?.('draw');
      const tool=completedDrawingTool(stroke.before.data,finished.data,stroke.engine.settings.kind,!!stroke.shape);
      if(tool)live.current.onToolUsed?.(tool);
    },
    onPointerCancel: event => { if (pointers.current.has(event.pointerId) || active.current?.id === event.pointerId) cancel(); },
    onLostPointerCapture: event => {
      // Child implicit capture may transfer to the wrapper. Its bubbled loss is
      // not loss by our owner; nor is a delayed release after a new capture.
      if (event.target !== event.currentTarget || inputOwner()?.hasPointerCapture(event.pointerId) || externalNavigation.current) return;
      if (pointers.current.has(event.pointerId) || active.current?.id === event.pointerId) cancel();
    }
  };
  const drawing = <canvas ref={canvas} width={dimensions.width} height={dimensions.height} style={{ aspectRatio: `${dimensions.width}/${dimensions.height}`, '--drawing-aspect': dimensions.width / dimensions.height } as React.CSSProperties} aria-label="Drawing canvas" aria-disabled={busy || inputDisabled} className={`drawing-canvas ${className}`} {...(!wallSurface ? pointerHandlers : {})} />;
  return wallSurface ? <div ref={wallInput} className="wall-drawing-layers" {...pointerHandlers}>{drawing}</div> : drawing;
});

const COLORS = [['#263f38','Ink'], ['#eee7d5','Cream'], ['#e68557','Copper'], ['#d85d6f','Rose'], ['#e3b654','Gold'], ['#75b994','Mint'], ['#5d95c4','Blue'], ['#b485c5','Lilac'], ['#ec92b2','Pink'], ['#98ba52','Lime'], ['#428a83','Teal'], ['#8b6554','Cocoa']];
const BRUSHES = [['pen','Pen','✎'], ['marker','Marker','▰'], ['spray','Spray','⁙'], ['eraser','Eraser','◧'], ['fill','Fill','▨']] as const;
interface DrawingToolsProps {
  settings: BrushSettings; onSettings: (settings: BrushSettings) => void; busy?: boolean;
  canUndo?: boolean; canRedo?: boolean; drawn?: boolean;
  onUndo: () => void; onRedo: () => void; onClear: () => void;
  wallFill?: { respectsArt: boolean; onChange: (respectsArt: boolean) => void; ready: boolean; error: string; onRetry: () => void };
}
export function DrawingTools({ settings, onSettings, busy, canUndo, canRedo, drawn, onUndo, onRedo, onClear, wallFill }: DrawingToolsProps) {
  const [customOpen, setCustomOpen] = useState(false);
  const change = (patch: Partial<BrushSettings>) => onSettings({ ...settings, ...patch });
  return <fieldset className="drawing-tools" disabled={busy}>
    <legend className="visually-hidden">Drawing tools</legend>
    <div className="brush-picker has-fill" role="group" aria-label="Brush type">{BRUSHES.map(([kind,label,icon]) => <button key={kind} type="button" className={settings.kind === kind ? 'active' : ''} aria-pressed={settings.kind === kind} onClick={() => change({ kind })}><span aria-hidden="true">{icon}</span>{label}</button>)}</div>
    <div className="brush-sliders"><label>Width <output>{settings.width}px</output><input aria-label="Brush width" disabled={settings.kind === 'fill'} type="range" min="2" max="72" value={settings.width} onChange={event => change({ width: Number(event.target.value) })}/></label><label>Opacity <output>{Math.round(settings.opacity * 100)}%</output><input aria-label="Brush opacity" type="range" min="10" max="100" step="5" value={Math.round(settings.opacity * 100)} onChange={event => change({ opacity: Number(event.target.value) / 100 })}/></label></div>
    {settings.kind === 'fill' && <>
      {wallFill && <div className="fill-boundaries" role="group" aria-label="Fill boundaries"><span>Boundaries</span><button type="button" aria-pressed={wallFill.respectsArt} onClick={() => wallFill.onChange(true)}>All artwork</button><button type="button" aria-pressed={!wallFill.respectsArt} onClick={() => wallFill.onChange(false)}>This drawing only</button></div>}
      <p className="fill-help">{wallFill ? `${wallFill.respectsArt?'Saved artwork and this drawing form the boundaries.':'Only this drawing forms the boundaries; saved artwork is ignored.'} Fill stays inside the selected panel.` : 'Tap a region to fill it. On an empty canvas, Fill colors the background. Undo brings it back.'}</p>
      {wallFill?.respectsArt && !wallFill.ready && <div className="fill-status" role="status">{wallFill.error || 'Loading saved wall artwork…'}{wallFill.error && <button type="button" onClick={wallFill.onRetry}>Retry</button>}</div>}
    </>}
    <div className="drawing-palette" role="group" aria-label="Paint colors">{COLORS.map(([color,name]) => <button key={color} type="button" className={`paint-swatch ${settings.color === color ? 'selected' : ''}`} style={{ backgroundColor: color }} onClick={() => change({ color })} aria-label={`${name} paint`} aria-pressed={settings.color === color}/>)}<button type="button" className="custom-paint-color" aria-label="Add custom color" onClick={() => setCustomOpen(true)}><span>＋</span></button></div>
    <div className="drawing-history"><button type="button" disabled={busy || !canUndo} onClick={onUndo}>↶ Undo</button><button type="button" disabled={busy || !canRedo} onClick={onRedo}>↷ Redo</button><button type="button" disabled={busy || !drawn} onClick={onClear}>Clear</button><small>Transparent background</small></div>
    {customOpen && <ColorPicker color={settings.color} onClose={() => setCustomOpen(false)} onChange={color => change({ color })}/> }
  </fieldset>;
}

export interface WallDrawingToolsProps extends DrawingToolsProps {
  surfaceName: string; estimatedInk: number; viewControls?: ReactNode;
  eraserControls?:ReactNode;
  canCancel?: boolean;
  showLayerControls?: boolean;
  studio?: boolean;
  error: string; canFinish: boolean; preparing?: boolean; preparationLocked?: boolean; finishLabel?: string; editLocked?: boolean; cancelLocked?:boolean;
  panning: boolean; onPanning: (panning: boolean) => void;
  onCancel: () => void; onFinish: () => void;
  /** Optional street-only visual action immediately before confirmation. */
  finishAction?: ReactNode;
  /** Optional street navigation action after layer controls, separate from confirmation. */
  navigationAction?: ReactNode;
  shadow: DrawingShadow; onShadow: (shadow: DrawingShadow) => void;
  layerIndex: number; layerCount: number; onLayerMove: (direction: 'up' | 'down') => void;
  chooser?: ReactNode; family?: 'draw' | 'stamp' | 'image'; familyControls?: ReactNode; onBack?: () => void;
  toolsOpen?: boolean;
  onCollapseTools?: () => void;
  desktop?: boolean;
  shape?: DrawingShape | null; onShape?: (shape: DrawingShape | null) => void;
  onReset?: () => void; onSample?: () => void; sampling?: boolean;
}

export { default as WallDrawingTools } from './CompactDrawingDock';
let rememberedDrawing: { image: string; width: number; height: number } | null = null;
let rememberedSettings = DEFAULT_BRUSH;
export function Drawing({ onSave, busy, initialAsset, saveLabel = 'Use drawing', onStateChange }: { onSave: (blob: Blob) => void; busy: boolean; initialAsset?: Asset | null; saveLabel?: string; onStateChange?: (drawn: boolean) => void }) {
  const canvas = useRef<DrawingCanvasHandle>(null);
  const [settings, setSettings] = useState(rememberedSettings);
  const [state, setState] = useState<Pick<DrawingState, 'drawn' | 'canUndo' | 'canRedo' | 'drawing'>>({ drawn: false, canUndo: false, canRedo: false, drawing: false });
  const [exporting, setExporting] = useState(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const locked = busy || exporting;
  return <div className="drawing-workspace"><p className="panel-copy drawing-intro">Pen for crisp lines, marker for translucent color, spray for soft mist. Only your paint reaches the wall.</p>
    <DrawingCanvas ref={canvas} settings={settings} busy={locked} width={initialAsset?.width || (initialAsset === undefined ? rememberedDrawing?.width : 0) || 768} height={initialAsset?.height || (initialAsset === undefined ? rememberedDrawing?.height : 0) || 384} initialImage={initialAsset?.url || (initialAsset === undefined ? rememberedDrawing?.image : null)} onRemember={(image, dimensions) => { rememberedDrawing = { image, ...dimensions }; }} onChange={next => { setState({ drawn: next.drawn, canUndo: next.canUndo, canRedo: next.canRedo, drawing: next.drawing }); onStateChange?.(next.drawn); }}/>
    <div className="drawing-rotation" role="group" aria-label="Rotate drawing"><span>Rotate artwork</span><button type="button" aria-label="Rotate drawing counterclockwise" disabled={locked || state.drawing || !state.drawn} onClick={() => canvas.current?.rotate('counterclockwise')}>↶ 90°</button><button type="button" aria-label="Rotate drawing clockwise" disabled={locked || state.drawing || !state.drawn} onClick={() => canvas.current?.rotate('clockwise')}>↷ 90°</button></div>
    <DrawingTools settings={settings} onSettings={next => { rememberedSettings = next; setSettings(next); }} busy={locked || state.drawing} {...state} onUndo={() => canvas.current?.undo()} onRedo={() => canvas.current?.redo()} onClear={() => canvas.current?.clear()}/>
    <div className="drawing-save"><small>Editing is free. Ink is charged after placement.</small><button className="primary-button" disabled={!state.drawn || locked || state.drawing} onClick={async () => { setExporting(true); const blob = await canvas.current?.getBlob(); if (!mounted.current) return; if (blob) onSave(blob); setExporting(false); }}>{locked ? 'Saving…' : saveLabel} <span>↗</span></button></div>
  </div>;
}
